import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';
import { formatDate } from '../utils/AuthUtils';
import Pagination from './Pagination';
import useIsMobile from '../hooks/useIsMobile';
import { calculateInvoiceTotals, calculateLine, resolveVatRate } from '../utils/vat';
import { branchVatRate } from '../utils/vatConfig';
import { printDocument } from '../utils/printDocument';
import { generateReceiptHTML } from '../utils/receiptHtml';
import {
  Receipt, Plus, Eye, Printer, X, Save, Search,
  CreditCard, Banknote, Building2, Shield, DollarSign,
  Trash2, FileText, Edit2,
  CheckCircle, Clock, AlertCircle
} from 'lucide-react';

// Format currency with proper number handling
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Billing = ({ currentBranch, user }) => {
  // A treatment with no rate override of its own inherits the branch rate.
  const branchRate = branchVatRate(currentBranch);
  const { print_lang, currentLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const printRef = useRef(null);

  // Data states
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [unbilledRecords, setUnbilledRecords] = useState([]);
  const [unbilledPrescriptions, setUnbilledPrescriptions] = useState([]);
  const [unbilledSales, setUnbilledSales] = useState([]);
  // The sources whose lines are currently on the form, kept whole (not just as
  // ids) so removing the last line of one can put its card back on the unbilled
  // panel. The SERVER derives what an invoice claims from the lines it is
  // actually given (utils/invoiceLinkage.js), so this is presentation state
  // only -- it can no longer drift out of step with what gets billed.
  const [importedRecords, setImportedRecords] = useState([]);
  const [importedRx, setImportedRx] = useState([]);
  const [importedSales, setImportedSales] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [treatmentSearch, setTreatmentSearch] = useState('');
  const [showTreatmentDropdown, setShowTreatmentDropdown] = useState(false);

  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('invoices'); // 'invoices', 'create', 'receipts'

  // Receipts tab. Its own filters and paging, kept separate from the invoice
  // list's so switching tabs does not reshuffle the other one.
  const [receipts, setReceipts] = useState([]);
  const [receiptSummary, setReceiptSummary] = useState({ by_method: {}, total: 0, count: 0 });
  const [receiptPage, setReceiptPage] = useState(1);
  const [receiptPageSize, setReceiptPageSize] = useState(50);
  const [receiptTotalPages, setReceiptTotalPages] = useState(1);
  const [receiptTotalCount, setReceiptTotalCount] = useState(0);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptFilters, setReceiptFilters] = useState({
    from_date: '', to_date: '', search: '', payment_method: '',
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  // What actually happened on the last print. `warning` means it printed, but
  // through the browser when the clinic had configured a printer; `error`
  // means nothing printed at all. Both keep the modal open so the message is
  // read rather than dismissed with it.
  const [printWarning, setPrintWarning] = useState('');
  const [printError, setPrintError] = useState('');
  const [printType, setPrintType] = useState('invoice'); // 'invoice' or 'receipt'
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(null); // Invoice being edited

  // Invoice creation form
  const [invoiceForm, setInvoiceForm] = useState({
    patient_id: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    items: [],
    discount_type: 'fixed',
    discount_amount: 0,
    tax_rate: 0,
    notes: ''
  });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    payment_reference: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Payment methods
  const paymentMethods = [
    { id: 'cash', name: 'เงินสด', name_en: 'Cash', icon: Banknote },
    { id: 'transfer', name: 'โอนเงิน', name_en: 'Bank Transfer', icon: Building2 },
    { id: 'credit_card', name: 'บัตรเครดิต', name_en: 'Credit Card', icon: CreditCard },
    { id: 'debit_card', name: 'บัตรเดบิต', name_en: 'Debit Card', icon: CreditCard },
    { id: 'social_security', name: 'ประกันสังคม', name_en: 'Social Security', icon: Shield },
    { id: 'insurance', name: 'ประกัน', name_en: 'Insurance', icon: Shield }
  ];

  useEffect(() => {
    if (currentBranch?.branch_id) {
      fetchInvoices();
      fetchPatients();
      fetchTreatments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch, statusFilter, currentPage, pageSize]);

  useEffect(() => {
    if (activeTab === 'receipts') fetchReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentBranch, receiptPage, receiptPageSize]);

  // Only loaded when the tab is actually open: a front desk that never opens it
  // should not pay for the query on every Billing visit.
  const fetchReceipts = async () => {
    if (!currentBranch?.branch_id) return;
    setReceiptsLoading(true);
    try {
      const params = new URLSearchParams({
        branch_id: currentBranch.branch_id,
        page: receiptPage,
        limit: receiptPageSize,
      });
      for (const [k, v] of Object.entries(receiptFilters)) {
        if (v) params.append(k, v);
      }
      const data = await fetchWithAuth(`/api/invoices/receipts?${params}`);
      setReceipts(data.data || []);
      setReceiptSummary(data.summary || { by_method: {}, total: 0, count: 0 });
      setReceiptTotalPages(data.pagination?.totalPages || 1);
      setReceiptTotalCount(data.pagination?.totalCount || 0);
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
      setReceipts([]);
      setReceiptSummary({ by_method: {}, total: 0, count: 0 });
    } finally {
      setReceiptsLoading(false);
    }
  };

  const methodLabel = (id) => {
    const m = paymentMethods.find((x) => x.id === id);
    if (!m) return id;
    return currentLanguage === 'th' ? m.name : m.name_en;
  };

  const fetchInvoices = async (resetPage = false) => {
    setIsLoading(true);
    try {
      const page = resetPage ? 1 : currentPage;
      if (resetPage) setCurrentPage(1);

      const params = new URLSearchParams({
        branch_id: currentBranch.branch_id,
        page: page,
        limit: pageSize,
        ...(statusFilter !== 'all' && { status: statusFilter })
      });
      const data = await fetchWithAuth(`/api/invoices?${params}`);
      setInvoices(data.data || []);

      if (data.pagination) {
        setTotalCount(data.pagination.totalCount || 0);
        setTotalPages(data.pagination.totalPages || 0);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const fetchPatients = async () => {
    try {
      const data = await fetchWithAuth(`/api/patients?branch_id=${currentBranch.branch_id}&status=active&limit=500`);
      setPatients(data.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const fetchTreatments = async () => {
    try {
      const data = await fetchWithAuth(`/api/treatments?branch_id=${currentBranch.branch_id}&status=active`);
      // API returns array directly, not wrapped in data object
      setTreatments(Array.isArray(data) ? data : (data.data || []));
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
    }
  };

  const fetchUnbilledRecords = async (patientId) => {
    try {
      const data = await fetchWithAuth(`/api/invoices/patient/${patientId}/unbilled`);
      setUnbilledRecords(data.data || []);
      setUnbilledPrescriptions(data.prescriptions || []);
      setUnbilledSales(data.product_sales || []);
    } catch (error) {
      console.error('Failed to fetch unbilled records:', error);
      setUnbilledRecords([]);
      setUnbilledPrescriptions([]);
      setUnbilledSales([]);
    }
  };

  const fetchInvoicePayments = async (invId) => {
    try {
      const data = await fetchWithAuth(`/api/invoices/${invId}/payments`);
      setInvoicePayments(data.data || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setInvoicePayments([]);
    }
  };

  // Handle patient selection
  const handlePatientChange = (patientId) => {
    setInvoiceForm({ ...invoiceForm, patient_id: patientId, items: [] });
    setImportedRecords([]);
    setImportedRx([]);
    setImportedSales([]);
    if (patientId) {
      fetchUnbilledRecords(patientId);
    } else {
      setUnbilledRecords([]);
      setUnbilledPrescriptions([]);
      setUnbilledSales([]);
    }
  };

  // Add unbilled record to invoice
  const handleAddUnbilledRecord = (record) => {
    const newItem = {
      item_type: 'treatment',
      item_name: record.treatment_name,
      item_description: record.tooth_number ? `Tooth #${record.tooth_number}` : '',
      tooth_number: record.tooth_number,
      quantity: 1,
      unit_price: record.cost || record.unit_price || 0,
      discount_type: 'fixed',
      discount_amount: 0,
      discount_percent: 0,
      // Frozen on the record when the dentist finished the treatment. Bill the
      // rule that applied on the visit day, not what the catalog says today.
      vat_mode: record.vat_mode || 'none',
      vat_rate: parseFloat(record.vat_rate) || 0,
      dentist_id: record.dentist_id,
      dentist_name: record.dentist_name,
      df_amount: record.df_amount || 0,
      reference_id: record.tmr_id,
      service_date: record.date
    };

    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Remove from unbilled list
    setImportedRecords(prev => [...prev, record]);
    setUnbilledRecords(prev => prev.filter(r => r.tmr_id !== record.tmr_id));
  };

  // Import a dispensed prescription's filled lines into the invoice
  const importPrescription = (rx) => {
    const lines = rx.items.map((i) => ({
      item_type: 'medication',
      item_name: `${i.medication_name}${i.strength ? ` ${i.strength}` : ''}`,
      item_description: `${rx.prescription_number} · ${i.billable_quantity} ${i.unit}`,
      quantity: i.billable_quantity,
      unit_price: parseFloat(i.unit_price) || 0,
      // The discount is pro-rated to the dispensed quantity by the API
      // (billable_discount_amount); discount_amount is the full-line figure
      // and only used as a fallback if that's somehow missing.
      discount_amount: i.billable_discount_amount !== undefined
        ? parseFloat(i.billable_discount_amount) || 0
        : parseFloat(i.discount_amount) || 0,
      // The rate was frozen when the line was written; bill what was quoted,
      // not what the catalog says today.
      vat_mode: i.vat_mode || 'none',
      vat_rate: parseFloat(i.vat_rate) || 0,
      reference_id: i.item_id,
      dentist_id: rx.dentist_id,
    }));

    setInvoiceForm((prev) => ({ ...prev, items: [...prev.items, ...lines] }));
    setImportedRx((prev) => [...prev, rx]);
    setUnbilledPrescriptions((prev) =>
      prev.filter((r) => r.prescription_id !== rx.prescription_id));
  };

  // Import a confirmed product sale's lines into the invoice
  const importSale = (sale) => {
    const lines = sale.items.map((i) => ({
      item_type: 'product',
      item_name: i.product_name,
      item_description: `${sale.sale_number} · ${i.quantity} ${i.unit}`,
      quantity: parseFloat(i.quantity) || 0,
      unit_price: parseFloat(i.unit_price) || 0,
      discount_amount: parseFloat(i.discount_amount) || 0,
      vat_mode: i.vat_mode || 'none',
      vat_rate: parseFloat(i.vat_rate) || 0,
      reference_id: i.item_id,
      dentist_id: sale.dentist_id,
    }));

    setInvoiceForm((prev) => ({ ...prev, items: [...prev.items, ...lines] }));
    setImportedSales((prev) => [...prev, sale]);
    setUnbilledSales((prev) => prev.filter((s) => s.sale_id !== sale.sale_id));
  };

  // Add manual item
  const handleAddManualItem = () => {
    const newItem = {
      item_type: 'other',
      item_name: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      discount_type: 'fixed',
      discount_amount: 0,
      discount_percent: 0,
      dentist_id: null,
      reference_id: null
    };

    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Add item from treatment selection
  const handleAddTreatmentItem = (treatment) => {
    const newItem = {
      item_type: 'treatment',
      item_name: treatment.name,
      item_description: treatment.description || '',
      quantity: 1,
      unit_price: parseFloat(treatment.price) || 0,
      discount_type: 'fixed',
      discount_amount: 0,
      discount_percent: 0,
      // Added straight from the catalog, so there is no frozen rate to honour —
      // resolve the catalog's intent against the branch rate now, so this line
      // prices identically to the same treatment imported from a visit.
      vat_mode: treatment.vat_mode || 'none',
      vat_rate: resolveVatRate({
        vatMode: treatment.vat_mode,
        itemRate: treatment.vat_rate,
        branchRate,
      }),
      dentist_id: null,
      reference_id: null,
      treatment_id: treatment.t_id  // Use t_id from treatments table
    };

    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setTreatmentSearch('');
    setShowTreatmentDropdown(false);
  };

  // Filter treatments for search
  const filteredTreatments = treatments.filter(t => {
    if (!treatmentSearch) return true;
    const search = treatmentSearch.toLowerCase();
    return (
      t.name?.toLowerCase().includes(search) ||
      t.name_en?.toLowerCase().includes(search) ||
      t.code?.toLowerCase().includes(search)
    );
  });

  // Update item
  const handleUpdateItem = (index, field, value) => {
    const updatedItems = [...invoiceForm.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Recalculate item discount if percentage changed
    if (field === 'discount_percent') {
      const item = updatedItems[index];
      const subtotal = item.quantity * item.unit_price;
      updatedItems[index].discount_amount = (subtotal * parseFloat(value)) / 100;
      updatedItems[index].discount_type = 'percentage';
    } else if (field === 'discount_amount') {
      updatedItems[index].discount_type = 'fixed';
      updatedItems[index].discount_percent = 0;
    }

    setInvoiceForm(prev => ({ ...prev, items: updatedItems }));
  };

  // Remove item.
  //
  // Removing an imported line has to release its source, or the operator is
  // left with a prescription that is neither billed nor billable: it is gone
  // from the unbilled panel with no way to put it back. The server already
  // derives linkage from the persisted lines, so this only has to keep the
  // panel honest.
  const handleRemoveItem = (index) => {
    const removed = invoiceForm.items[index];
    const remaining = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm(prev => ({ ...prev, items: remaining }));
    if (removed) releaseSourceOf(removed, remaining);
  };

  // Put a source's card back once the invoice holds none of its lines.
  const releaseSourceOf = (removed, remaining) => {
    const refId = Number(removed.reference_id);
    if (!refId) return;
    const type = (removed.item_type || '').toLowerCase();

    if (type === 'treatment') {
      const stillUsed = remaining.some(
        (i) => (i.item_type || '').toLowerCase() === 'treatment' && Number(i.reference_id) === refId);
      if (stillUsed) return;
      const rec = importedRecords.find((r) => Number(r.tmr_id) === refId);
      if (!rec) return;
      setImportedRecords((prev) => prev.filter((r) => Number(r.tmr_id) !== refId));
      setUnbilledRecords((prev) =>
        prev.some((r) => Number(r.tmr_id) === refId) ? prev : [...prev, rec]);
      return;
    }

    // A prescription/sale line's reference_id is its LINE id, so the owning
    // document is the one whose items include it.
    if (type === 'medication' || type === 'product') {
      const isRx = type === 'medication';
      const pool = isRx ? importedRx : importedSales;
      const key = isRx ? 'prescription_id' : 'sale_id';
      const owner = pool.find((doc) =>
        (doc.items || []).some((i) => Number(i.item_id) === refId));
      if (!owner) return;
      const ownerLineIds = (owner.items || []).map((i) => Number(i.item_id));
      const stillUsed = remaining.some(
        (i) => (i.item_type || '').toLowerCase() === type
          && ownerLineIds.includes(Number(i.reference_id)));
      if (stillUsed) return;
      const setPool = isRx ? setImportedRx : setImportedSales;
      const setPanel = isRx ? setUnbilledPrescriptions : setUnbilledSales;
      setPool((prev) => prev.filter((d) => d[key] !== owner[key]));
      setPanel((prev) => (prev.some((d) => d[key] === owner[key]) ? prev : [...prev, owner]));
    }
  };

  // Items shaped for the shared VAT module (snake_case, numeric, defaulted)
  const buildVatItems = () => invoiceForm.items.map((item) => ({
    quantity: item.quantity || 1,
    unit_price: item.unit_price,
    discount_amount: parseFloat(item.discount_amount) || 0,
    vat_mode: item.vat_mode || 'none',
    vat_rate: parseFloat(item.vat_rate) || 0,
  }));

  // The invoice-level discount can be entered as a fixed amount or as a
  // percentage. calculateInvoiceTotals only accepts a flat amount, so a
  // percentage is resolved against the net subtotal (after each line's own
  // discount) — run once with no discount just to get that subtotal.
  const resolveInvoiceDiscount = (vatItems) => {
    const raw = parseFloat(invoiceForm.discount_amount) || 0;
    if (invoiceForm.discount_type !== 'percentage') return raw;
    const netSubtotal = calculateInvoiceTotals({ items: vatItems, invoiceDiscount: 0, invoiceTaxRate: 0 }).subtotal;
    return (netSubtotal * raw) / 100;
  };

  // Calculate totals — one source of truth shared with the API (see
  // API/utils/vat.js). With every item at vat_mode 'none' this reproduces the
  // arithmetic this screen always used; VAT-bearing lines are new.
  const calculateTotals = () => {
    const vatItems = buildVatItems();
    return calculateInvoiceTotals({
      items: vatItems,
      invoiceDiscount: resolveInvoiceDiscount(vatItems),
      invoiceTaxRate: Number(invoiceForm.tax_rate) || 0,
    });
  };

  // Calculate one item's line (net/vat/total) via the shared VAT module
  const calculateItemLine = (item) => calculateLine({
    quantity: item.quantity || 1,
    unit_price: item.unit_price,
    discount_amount: parseFloat(item.discount_amount) || 0,
    vat_mode: item.vat_mode || 'none',
    vat_rate: parseFloat(item.vat_rate) || 0,
  });

  // Calculate item total (VAT-inclusive, matches what the API will store)
  const calculateItemTotal = (item) => calculateItemLine(item).total;

  // Create or Update invoice
  const handleSaveInvoice = async () => {
    if (!invoiceForm.patient_id) {
      alert(print_lang('select_patient', 'Please select a patient'));
      return;
    }

    if (invoiceForm.items.length === 0) {
      alert(print_lang('add_items', 'Please add at least one item'));
      return;
    }

    setIsSaving(true);
    try {
      const totals = calculateTotals();

      const payload = {
        patient_id: invoiceForm.patient_id,
        date: invoiceForm.date,
        due_date: invoiceForm.due_date || null,
        items: invoiceForm.items.map(item => ({
          ...item,
          total_price: calculateItemTotal(item)
        })),
        discount_amount: totals.discount,
        discount_type: invoiceForm.discount_type,
        tax_rate: invoiceForm.tax_rate,
        notes: invoiceForm.notes,
        branch_id: currentBranch.branch_id
      };

      let result;
      if (editingInvoice) {
        // Update existing invoice
        result = await fetchWithAuth(`/api/invoices/${editingInvoice.inv_id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        alert(print_lang('invoice_updated', 'Invoice updated successfully'));
      } else {
        // Create new invoice
        result = await fetchWithAuth('/api/invoices', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        alert(`${print_lang('invoice_created', 'Invoice created')}: ${result.invoice_number}`);
      }

      // Reset form and switch to list
      setEditingInvoice(null);
      setInvoiceForm({
        patient_id: '',
        date: new Date().toISOString().split('T')[0],
        due_date: '',
        items: [],
        discount_type: 'fixed',
        discount_amount: 0,
        tax_rate: 0,
        notes: ''
      });
      setUnbilledRecords([]);
      setUnbilledPrescriptions([]);
      setUnbilledSales([]);
      setImportedRecords([]);
      setImportedRx([]);
      setImportedSales([]);
      setActiveTab('invoices');
      fetchInvoices();

    } catch (error) {
      console.error('Failed to save invoice:', error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // View invoice details
  const handleViewInvoice = async (invoice) => {
    try {
      const data = await fetchWithAuth(`/api/invoices/${invoice.inv_id}`);
      setSelectedInvoice(data.data);
      await fetchInvoicePayments(invoice.inv_id);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
    }
  };

  // Edit invoice (only for pending status)
  const handleEditInvoice = async (invoice) => {
    if (invoice.status !== 'pending') {
      alert(print_lang('cannot_edit_invoice', 'Only pending invoices can be edited'));
      return;
    }

    try {
      const data = await fetchWithAuth(`/api/invoices/${invoice.inv_id}`);
      const invoiceData = data.data;

      // Load invoice data into form
      setInvoiceForm({
        patient_id: invoiceData.patient_id?.toString() || '',
        date: invoiceData.date ? invoiceData.date.split('T')[0] : new Date().toISOString().split('T')[0],
        due_date: invoiceData.due_date ? invoiceData.due_date.split('T')[0] : '',
        items: (invoiceData.items || []).map(item => ({
          item_id: item.item_id,
          item_type: item.item_type || 'treatment',
          item_name: item.item_name || '',
          item_description: item.item_description || '',
          quantity: item.quantity || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_type: item.discount_type || 'fixed',
          discount_amount: parseFloat(item.discount_amount) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          vat_mode: item.vat_mode || 'none',
          vat_rate: parseFloat(item.vat_rate) || 0,
          dentist_id: item.dentist_id,
          reference_id: item.reference_id,
          treatment_id: item.treatment_id
        })),
        discount_type: invoiceData.discount_type || 'fixed',
        discount_amount: parseFloat(invoiceData.discount_amount) || 0,
        tax_rate: parseFloat(invoiceData.tax_rate) || 0,
        notes: invoiceData.notes || ''
      });

      setEditingInvoice(invoiceData);
      // Clear unbilled records/imports when editing — an existing invoice's
      // items were already billed, so there is nothing left to import.
      setUnbilledRecords([]);
      setUnbilledPrescriptions([]);
      setUnbilledSales([]);
      setImportedRecords([]);
      setImportedRx([]);
      setImportedSales([]);
      setActiveTab('create');
    } catch (error) {
      console.error('Failed to load invoice for editing:', error);
      alert(print_lang('error_loading_invoice', 'Failed to load invoice'));
    }
  };

  // Cancel editing and reset form
  const handleCancelEdit = () => {
    setEditingInvoice(null);
    setInvoiceForm({
      patient_id: '',
      date: new Date().toISOString().split('T')[0],
      due_date: '',
      items: [],
      discount_type: 'fixed',
      discount_amount: 0,
      tax_rate: 0,
      notes: ''
    });
    setUnbilledRecords([]);
    setUnbilledPrescriptions([]);
    setUnbilledSales([]);
    setImportedRecords([]);
    setImportedRx([]);
    setImportedSales([]);
    setActiveTab('invoices');
  };

  // Delete invoice (only for pending status)
  const handleDeleteInvoice = async (invoice) => {
    if (invoice.status !== 'pending') {
      alert(print_lang('cannot_delete_invoice', 'Only pending invoices can be deleted'));
      return;
    }

    if (!window.confirm(print_lang('confirm_delete_invoice', `Are you sure you want to delete invoice ${invoice.invoice_number}?`))) {
      return;
    }

    try {
      await fetchWithAuth(`/api/invoices/${invoice.inv_id}`, {
        method: 'DELETE'
      });

      alert(print_lang('invoice_deleted', 'Invoice deleted successfully'));
      fetchInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      alert(error.message || print_lang('error_deleting_invoice', 'Failed to delete invoice'));
    }
  };

  // Open payment modal
  const handleOpenPayment = (invoice) => {
    // Check if invoice is already fully paid
    if (invoice.status === 'paid' || invoice.status === 'cancelled') {
      alert(print_lang('invoice_already_paid', 'This invoice is already fully paid or cancelled'));
      return;
    }

    const balance = parseFloat(invoice.amount || 0) - parseFloat(invoice.paid_amount || 0);
    if (balance <= 0) {
      alert(print_lang('invoice_already_paid', 'This invoice is already fully paid'));
      return;
    }

    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: balance,
      payment_method: 'cash',
      payment_reference: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowPaymentModal(true);
  };

  // Record payment
  const handleRecordPayment = async () => {
    // Double-check invoice isn't already paid
    const currentBalance = parseFloat(selectedInvoice.amount || 0) - parseFloat(selectedInvoice.paid_amount || 0);
    if (currentBalance <= 0 || selectedInvoice.status === 'paid') {
      alert(print_lang('invoice_already_paid', 'This invoice is already fully paid'));
      setShowPaymentModal(false);
      fetchInvoices();
      return;
    }

    if (!paymentForm.amount || paymentForm.amount <= 0) {
      alert(print_lang('invalid_amount', 'Please enter a valid amount'));
      return;
    }

    setIsSaving(true);
    try {
      const result = await fetchWithAuth(`/api/invoices/${selectedInvoice.inv_id}/payment`, {
        method: 'POST',
        body: JSON.stringify(paymentForm)
      });

      alert(`${print_lang('payment_recorded', 'Payment recorded')}: ${result.receipt_number}`);
      setShowPaymentModal(false);
      fetchInvoices();

      // Refresh invoice modal if open
      if (showInvoiceModal) {
        handleViewInvoice({ inv_id: selectedInvoice.inv_id });
      }

    } catch (error) {
      console.error('Failed to record payment:', error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Print invoice
  const handlePrintInvoice = async (invoice) => {
    try {
      const data = await fetchWithAuth(`/api/invoices/${invoice.inv_id}`);
      setSelectedInvoice(data.data);
      setPrintType('invoice');
      setPrintWarning('');
      setPrintError('');
      setShowPrintModal(true);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
    }
  };

  // Print receipt
  const handlePrintReceipt = async (payment) => {
    try {
      const data = await fetchWithAuth(`/api/invoices/receipts/${payment.payment_id}`);
      setSelectedPayment(data.data);
      setPrintType('receipt');
      setPrintWarning('');
      setPrintError('');
      setShowPrintModal(true);
    } catch (error) {
      console.error('Failed to fetch receipt:', error);
    }
  };

  // Execute print
  //
  // Two documents, two printers:
  //   invoice -> the branch's A4 `document` printer, from the preview above
  //   receipt -> the branch's 80mm `receipt` printer, from receiptHtml.js
  // A receipt is NOT the A4 preview shrunk: a 210mm layout on a thermal head
  // prints garbled or blank, which is why it has a template of its own.
  //
  // Each is built twice. The agent's copy carries no auto-print script — the
  // agent prints the document itself, and a window.print() inside it would
  // fire a second print in its hidden window. The browser copy needs the
  // script, because nothing else is going to press print.
  const AUTO_PRINT_SCRIPT = `
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); window.onafterprint = function() { window.close(); }; }, 500);
          };
        </script>`;

  const buildInvoiceHtml = (autoPrint) => {
    const printContent = printRef.current;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${printType === 'invoice' ? 'Invoice' : 'Receipt'} - ${selectedInvoice?.invoice_number || selectedPayment?.receipt_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Sarabun', sans-serif; font-size: 14px; line-height: 1.5; color: #000; background: #fff; }
          .document { width: 210mm; min-height: 297mm; padding: 10mm 15mm; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #000; }
          .logo { height: 60px; }
          .company-info { text-align: right; }
          .company-name { font-size: 20px; font-weight: bold; }
          .doc-title { text-align: center; font-size: 24px; font-weight: bold; margin: 20px 0; padding: 10px; background: #f0f0f0; }
          .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .info-box { width: 48%; }
          .info-label { font-weight: 600; color: #666; font-size: 12px; }
          .info-value { font-size: 14px; margin-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: 600; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .totals { width: 50%; margin-left: auto; margin-top: 20px; }
          .totals tr td { border: none; padding: 5px 10px; }
          .totals .grand-total { font-size: 18px; font-weight: bold; background: #f0f0f0; }
          .signature-section { display: flex; justify-content: space-between; margin-top: 50px; padding-top: 30px; }
          .signature-box { width: 40%; text-align: center; }
          .signature-line { border-top: 1px solid #000; margin-top: 60px; padding-top: 10px; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; text-align: center; font-size: 12px; color: #666; }
          .status-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; font-weight: 600; }
          .status-paid { background: #d4edda; color: #155724; }
          .status-pending { background: #fff3cd; color: #856404; }
          .status-partial { background: #cce5ff; color: #004085; }
          @media print {
            @page { size: A4; margin: 0; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body>
        ${printContent ? printContent.innerHTML : ''}${autoPrint ? AUTO_PRINT_SCRIPT : ''}
      </body>
      </html>
    `;
  };

  const buildReceiptHtml = (autoPrint) => generateReceiptHTML(selectedPayment || {}, {
    print_lang,
    autoPrint,
    amountInWords: numberToThaiWords(selectedPayment?.amount || 0),
  });

  const executePrint = async () => {
    setPrintWarning('');
    setPrintError('');

    const isReceipt = printType === 'receipt';
    const build = isReceipt ? buildReceiptHtml : buildInvoiceHtml;

    const result = await printDocument({
      agentHtml: build(false),
      browserHtml: build(true),
      printerType: isReceipt ? 'receipt' : 'document',
      branch: currentBranch,
    });

    if (result.popupBlocked) {
      // Nothing printed. Say so and leave the modal open to be retried.
      setPrintError(print_lang('popup_blocked', 'Popup blocked — allow popups and try again'));
      return;
    }
    if (result.warned) {
      // It printed, but not the way the branch was set up to print. Worth
      // saying: the alternative is a clinic quietly losing silent printing and
      // never being told.
      setPrintWarning(print_lang('printed_via_browser', 'Print agent offline — printed via the browser'));
      return;
    }
    setShowPrintModal(false);
  };

  // Thai number to words
  const numberToThaiWords = (num) => {
    const units = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
    const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

    if (num === 0) return 'ศูนย์บาทถ้วน';

    const intPart = Math.floor(num);
    const decPart = Math.round((num - intPart) * 100);

    let result = '';
    let tempNum = intPart;
    let pos = 0;

    while (tempNum > 0) {
      const digit = tempNum % 10;
      if (digit !== 0) {
        if (pos === 1 && digit === 1) {
          result = 'สิบ' + result;
        } else if (pos === 1 && digit === 2) {
          result = 'ยี่สิบ' + result;
        } else if (pos === 0 && digit === 1 && intPart > 10) {
          result = 'เอ็ด' + result;
        } else {
          result = units[digit] + positions[pos] + result;
        }
      }
      tempNum = Math.floor(tempNum / 10);
      pos++;
    }

    result += 'บาท';

    if (decPart > 0) {
      if (decPart < 10) {
        result += units[decPart] + 'สตางค์';
      } else {
        const tens = Math.floor(decPart / 10);
        const ones = decPart % 10;
        if (tens === 1) {
          result += 'สิบ';
        } else if (tens === 2) {
          result += 'ยี่สิบ';
        } else {
          result += units[tens] + 'สิบ';
        }
        if (ones === 1) {
          result += 'เอ็ด';
        } else if (ones > 0) {
          result += units[ones];
        }
        result += 'สตางค์';
      }
    } else {
      result += 'ถ้วน';
    }

    return result;
  };

  // Format Thai date
  const formatThaiDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const thaiMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
    return `${date.getDate()} ${thaiMonths[date.getMonth()]} ${date.getFullYear() + 543}`;
  };

  // Status badge
  const statusColors = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
    paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    partial: { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: X }
  };

  const getStatusBadge = (status) => {
    const style = statusColors[status] || statusColors.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${style.bg} ${style.text}`}>
        <Icon size={14} />
        {print_lang(status, status)}
      </span>
    );
  };

  // Invoice list row pieces, shared by the table branch (desktop) and the
  // card branch (phone, <768px) so the two can never drift apart - see Task 8
  // brief. The conditions that gate edit/delete/payment live here once too,
  // not re-derived per layout.
  const invoiceBalance = (invoice) => (invoice.amount || 0) - (invoice.paid_amount || 0);
  const invoiceCanEditDelete = (invoice) => invoice.status === 'pending';
  const invoiceCanRecordPayment = (invoice) => invoice.status !== 'paid' && invoice.status !== 'cancelled';

  const invoiceViewButton = (invoice) => (
    <button
      onClick={() => handleViewInvoice(invoice)}
      className="p-2 hover:bg-gray-100 rounded"
      title={print_lang('view', 'View')}
    >
      <Eye size={16} className="text-gray-600" />
    </button>
  );

  const invoiceEditButton = (invoice) => (
    <button
      onClick={() => handleEditInvoice(invoice)}
      className="p-2 hover:bg-yellow-50 rounded"
      title={print_lang('edit', 'Edit')}
    >
      <Edit2 size={16} className="text-yellow-600" />
    </button>
  );

  const invoiceDeleteButton = (invoice) => (
    <button
      onClick={() => handleDeleteInvoice(invoice)}
      className="p-2 hover:bg-red-50 rounded"
      title={print_lang('delete', 'Delete')}
    >
      <Trash2 size={16} className="text-red-600" />
    </button>
  );

  // `labeled` swaps the compact icon-only button (table row, and the card's
  // secondary icon row) for a full-width text+icon button (the card's
  // primary action row) - same handler and title either way.
  const invoicePaymentButton = (invoice, { labeled = false } = {}) => (
    <button
      onClick={() => handleOpenPayment(invoice)}
      className={labeled ? 'btn btn-primary invoice-card-action-primary' : 'p-2 hover:bg-green-50 rounded'}
      title={print_lang('record_payment', 'Record Payment')}
    >
      <DollarSign size={16} className={labeled ? undefined : 'text-green-600'} />
      {labeled && print_lang('record_payment', 'Record Payment')}
    </button>
  );

  const invoicePrintButton = (invoice, { labeled = false } = {}) => (
    <button
      onClick={() => handlePrintInvoice(invoice)}
      className={labeled ? 'btn btn-secondary invoice-card-action-primary' : 'p-2 hover:bg-blue-50 rounded'}
      title={print_lang('print_invoice', 'Print Invoice')}
    >
      <Printer size={16} className={labeled ? undefined : 'text-blue-600'} />
      {labeled && print_lang('print_invoice', 'Print Invoice')}
    </button>
  );

  // Exactly what the table's actions cell rendered before Task 8.
  const invoiceRowActions = (invoice) => (
    <div className="flex gap-1 justify-end">
      {invoiceViewButton(invoice)}
      {invoiceCanEditDelete(invoice) && (
        <>
          {invoiceEditButton(invoice)}
          {invoiceDeleteButton(invoice)}
        </>
      )}
      {invoiceCanRecordPayment(invoice) && invoicePaymentButton(invoice)}
      {invoicePrintButton(invoice)}
    </div>
  );

  const filteredInvoices = invoices.filter(inv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(search) ||
      inv.patient_name?.toLowerCase().includes(search)
    );
  });

  // Calculate totals for display
  const totals = calculateTotals();

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Receipt size={28} />
          {print_lang('billing', 'Billing')}
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => {
            if (editingInvoice) {
              handleCancelEdit();
            } else {
              setActiveTab('invoices');
            }
          }}
          className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'invoices' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          <Receipt size={18} className="inline mr-2" />
          {print_lang('invoices', 'Invoices')}
        </button>
        <button
          onClick={() => {
            if (editingInvoice) {
              handleCancelEdit();
            }
            setActiveTab('create');
          }}
          className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'create' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          {editingInvoice ? (
            <>
              <Edit2 size={18} className="inline mr-2" />
              {print_lang('edit_invoice', 'Edit Invoice')}
            </>
          ) : (
            <>
              <Plus size={18} className="inline mr-2" />
              {print_lang('create_invoice', 'Create Invoice')}
            </>
          )}
        </button>
        <button
          onClick={() => {
            if (editingInvoice) handleCancelEdit();
            setActiveTab('receipts');
          }}
          className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'receipts' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          <Banknote size={18} className="inline mr-2" />
          {print_lang('receipts', 'Receipts')}
        </button>
        {editingInvoice && (
          <button
            onClick={handleCancelEdit}
            className="px-4 py-2 rounded-lg font-medium bg-red-100 hover:bg-red-200 text-red-700"
          >
            <X size={18} className="inline mr-2" />
            {print_lang('cancel_edit', 'Cancel Edit')}
          </button>
        )}
      </div>

      {/* Receipts Tab */}
      {activeTab === 'receipts' && (
        <div>
          {/* Filters */}
          <div className="card mb-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('from_date', 'From')}</label>
                <input
                  type="date"
                  value={receiptFilters.from_date}
                  onChange={(e) => setReceiptFilters({ ...receiptFilters, from_date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('to_date', 'To')}</label>
                <input
                  type="date"
                  value={receiptFilters.to_date}
                  onChange={(e) => setReceiptFilters({ ...receiptFilters, to_date: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">{print_lang('search', 'Search')}</label>
                <input
                  type="text"
                  placeholder={print_lang('search_receipt', 'Receipt no., invoice no. or patient')}
                  value={receiptFilters.search}
                  onChange={(e) => setReceiptFilters({ ...receiptFilters, search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setReceiptPage(1); fetchReceipts(); } }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('payment_method', 'Method')}</label>
                <select
                  value={receiptFilters.payment_method}
                  onChange={(e) => setReceiptFilters({ ...receiptFilters, payment_method: e.target.value })}
                >
                  <option value="">{print_lang('all', 'All')}</option>
                  {paymentMethods.map((m) => (
                    <option key={m.id} value={m.id}>{methodLabel(m.id)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setReceiptFilters({ from_date: '', to_date: '', search: '', payment_method: '' });
                  setReceiptPage(1);
                }}
              >
                {print_lang('clear', 'Clear')}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => { setReceiptPage(1); fetchReceipts(); }}
              >
                <Search size={16} className="inline mr-2" />
                {print_lang('search', 'Search')}
              </button>
            </div>
          </div>

          {/* Takings for the whole filter, not just this page — the number
              someone cashing up actually needs. */}
          <div className="card mb-4">
            <div className="flex flex-wrap items-center gap-6">
              {paymentMethods
                .filter((m) => receiptSummary.by_method[m.id])
                .map((m) => (
                  <div key={m.id}>
                    <p className="text-xs text-gray-500">{methodLabel(m.id)}</p>
                    <p className="font-medium">
                      {formatCurrency(receiptSummary.by_method[m.id].total)} ฿
                      <span className="text-xs text-gray-400 ml-1">
                        ({receiptSummary.by_method[m.id].count})
                      </span>
                    </p>
                  </div>
                ))}
              <div className="ml-auto text-right">
                <p className="text-xs text-gray-500">
                  {print_lang('total_received', 'Total received')}
                  <span className="ml-1">({receiptSummary.count})</span>
                </p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(receiptSummary.total)} ฿
                </p>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="card">
            {receiptsLoading ? (
              <p className="text-center text-gray-400 py-8">{print_lang('loading', 'Loading...')}</p>
            ) : receipts.length === 0 ? (
              <p className="text-center text-gray-400 py-8">{print_lang('no_receipts', 'No receipts found')}</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>{print_lang('receipt_number', 'Receipt No.')}</th>
                    <th>{print_lang('date', 'Date')}</th>
                    <th>{print_lang('patient', 'Patient')}</th>
                    <th>{print_lang('invoice', 'Invoice')}</th>
                    <th>{print_lang('payment_method', 'Method')}</th>
                    <th className="text-right">{print_lang('amount', 'Amount')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r) => (
                    <tr key={r.payment_id}>
                      <td className="font-mono text-sm">{r.receipt_number}</td>
                      <td>{formatDate(r.payment_date)}</td>
                      <td>{r.patient_name || '-'}</td>
                      <td className="font-mono text-sm">{r.invoice_number || '-'}</td>
                      <td>
                        {methodLabel(r.payment_method)}
                        {r.payment_reference && (
                          <span className="text-xs text-gray-400 block">{r.payment_reference}</span>
                        )}
                      </td>
                      <td className="text-right font-medium">{formatCurrency(r.amount)} ฿</td>
                      <td>
                        <div className="flex gap-1 justify-end items-center">
                          {/* A cancelled or refunded payment stays listed so the
                              receipt numbering has no unexplained gap, but it is
                              marked and left out of the takings above. */}
                          {r.status && r.status !== 'completed' && (
                            <span className="badge badge-danger mr-2">
                              {print_lang(r.status, r.status)}
                            </span>
                          )}
                          <button
                            className="p-2 hover:bg-gray-100 rounded"
                            title={print_lang('print', 'Print')}
                            onClick={() => handlePrintReceipt(r)}
                          >
                            <Printer size={16} className="text-blue-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {receiptTotalCount > 0 && (
              <Pagination
                currentPage={receiptPage}
                totalPages={receiptTotalPages}
                totalCount={receiptTotalCount}
                pageSize={receiptPageSize}
                onPageChange={setReceiptPage}
                onPageSizeChange={(size) => { setReceiptPageSize(size); setReceiptPage(1); }}
                print_lang={print_lang}
              />
            )}
          </div>
        </div>
      )}

      {/* Invoices List Tab */}
      {activeTab === 'invoices' && (
        <div>
          {/* Filters */}
          <div className="card mb-6">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">{print_lang('search', 'Search')}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={print_lang('search_invoice', 'Search invoice or patient...')}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('status', 'Status')}</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">{print_lang('all', 'All')}</option>
                  <option value="pending">{print_lang('pending', 'Pending')}</option>
                  <option value="paid">{print_lang('paid', 'Paid')}</option>
                  <option value="partial">{print_lang('partial', 'Partial')}</option>
                  <option value="cancelled">{print_lang('cancelled', 'Cancelled')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Invoices: table (desktop) / card list (phone, <768px) */}
          <div className="card overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : isMobile ? (
              <ul className="invoice-card-list">
                {filteredInvoices.length === 0 ? (
                  <li className="invoice-card-empty text-center text-gray-500 py-8">
                    {print_lang('no_data', 'No data')}
                  </li>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <li className="invoice-card" key={invoice.inv_id}>
                      <div className="invoice-card-top">
                        <span className="invoice-card-number font-mono">{invoice.invoice_number}</span>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <div className="invoice-card-meta">
                        <span className="invoice-card-patient">{invoice.patient_name}</span>
                        <span className="invoice-card-date">{formatDate(invoice.date)}</span>
                      </div>
                      <dl className="invoice-card-amounts">
                        <div className="invoice-card-amount">
                          <dt>{print_lang('total', 'Total')}</dt>
                          <dd className="font-medium">{formatCurrency(invoice.amount)} ฿</dd>
                        </div>
                        <div className="invoice-card-amount">
                          <dt>{print_lang('paid', 'Paid')}</dt>
                          <dd className="text-green-600">{formatCurrency(invoice.paid_amount)} ฿</dd>
                        </div>
                        <div className="invoice-card-amount">
                          <dt>{print_lang('balance', 'Balance')}</dt>
                          <dd className="text-red-600 font-medium">{formatCurrency(invoiceBalance(invoice))} ฿</dd>
                        </div>
                      </dl>
                      {/* Five actions do not fit one row at 390px. View/edit/delete
                          (secondary, and edit/delete only when pending) sit as
                          compact icons; record payment and print invoice - what
                          the front desk reaches for most - get their own
                          full-width labeled row underneath. */}
                      <div className="invoice-card-actions">
                        <div className="invoice-card-actions-icons">
                          {invoiceViewButton(invoice)}
                          {invoiceCanEditDelete(invoice) && (
                            <>
                              {invoiceEditButton(invoice)}
                              {invoiceDeleteButton(invoice)}
                            </>
                          )}
                        </div>
                        <div className="invoice-card-actions-primary">
                          {invoiceCanRecordPayment(invoice) && invoicePaymentButton(invoice, { labeled: true })}
                          {invoicePrintButton(invoice, { labeled: true })}
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>{print_lang('invoice_no', 'Invoice No.')}</th>
                    <th>{print_lang('date', 'Date')}</th>
                    <th>{print_lang('patient', 'Patient')}</th>
                    <th className="text-right">{print_lang('total', 'Total')}</th>
                    <th className="text-right">{print_lang('paid', 'Paid')}</th>
                    <th className="text-right">{print_lang('balance', 'Balance')}</th>
                    <th>{print_lang('status', 'Status')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-gray-500 py-8">
                        {print_lang('no_data', 'No data')}
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.inv_id}>
                        <td className="font-mono font-medium">{invoice.invoice_number}</td>
                        <td>{formatDate(invoice.date)}</td>
                        <td>{invoice.patient_name}</td>
                        <td className="text-right font-medium">{formatCurrency(invoice.amount)} ฿</td>
                        <td className="text-right text-green-600">{formatCurrency(invoice.paid_amount)} ฿</td>
                        <td className="text-right text-red-600 font-medium">{formatCurrency(invoiceBalance(invoice))} ฿</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                        <td>{invoiceRowActions(invoice)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {!isLoading && invoices.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                print_lang={print_lang}
              />
            )}
          </div>
        </div>
      )}

      {/* Create Invoice Tab */}
      {activeTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Invoice Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Edit Mode Indicator */}
            {editingInvoice && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                <Edit2 className="text-yellow-600" size={24} />
                <div>
                  <p className="font-medium text-yellow-800">
                    {print_lang('editing_invoice', 'Editing Invoice')}: {editingInvoice.invoice_number}
                  </p>
                  <p className="text-sm text-yellow-600">
                    {print_lang('edit_mode_notice', 'Make changes and click Update Invoice to save')}
                  </p>
                </div>
              </div>
            )}

            {/* Patient Selection */}
            <div className="card">
              <h3 className="text-lg font-bold mb-4">{print_lang('patient', 'Patient')}</h3>
              <select
                value={invoiceForm.patient_id}
                onChange={(e) => handlePatientChange(e.target.value)}
                className="w-full"
                disabled={!!editingInvoice}
              >
                <option value="">{print_lang('select_patient', 'Select Patient')}</option>
                {patients.map((patient) => (
                  <option key={patient.patient_id} value={patient.patient_id}>
                    {patient.prefix} {patient.name} {patient.phone ? `(${patient.phone})` : ''}
                  </option>
                ))}
              </select>
              {editingInvoice && (
                <p className="text-sm text-gray-500 mt-1">
                  {print_lang('patient_cannot_change', 'Patient cannot be changed when editing')}
                </p>
              )}
            </div>

            {/* Unbilled Treatments */}
            {invoiceForm.patient_id && unbilledRecords.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FileText size={20} />
                  {print_lang('unbilled_treatments', 'Unbilled Treatments')}
                </h3>
                <div className="space-y-2">
                  {unbilledRecords.map((record) => (
                    <div
                      key={record.tmr_id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-medium">{record.treatment_name}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(record.date)} • {record.dentist_name}
                          {record.tooth_number && ` • Tooth #${record.tooth_number}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{formatCurrency(record.cost || record.unit_price)} ฿</span>
                        <button
                          onClick={() => handleAddUnbilledRecord(record)}
                          className="btn btn-primary btn-sm"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unbilled Prescriptions (dispensed, not yet invoiced) */}
            {invoiceForm.patient_id && unbilledPrescriptions.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FileText size={20} />
                  {print_lang('unbilled_prescriptions', 'Unbilled Prescriptions')}
                </h3>
                <div className="space-y-2">
                  {unbilledPrescriptions.map((rx) => (
                    <div
                      key={rx.prescription_id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-medium">{rx.prescription_number}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(rx.prescription_date)} • {(rx.items || []).length} {print_lang('items', 'items')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* What was dispensed, not what was written — matches what saving will invoice */}
                        <span className="font-medium">{formatCurrency(rx.billable_total_amount)} ฿</span>
                        <button
                          onClick={() => importPrescription(rx)}
                          className="btn btn-primary btn-sm"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unbilled Product Sales (confirmed, not yet invoiced) */}
            {invoiceForm.patient_id && unbilledSales.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FileText size={20} />
                  {print_lang('unbilled_sales', 'Unbilled Product Sales')}
                </h3>
                <div className="space-y-2">
                  {unbilledSales.map((sale) => (
                    <div
                      key={sale.sale_id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-medium">{sale.sale_number}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(sale.sale_date)} • {(sale.items || []).length} {print_lang('items', 'items')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{formatCurrency(sale.total_amount)} ฿</span>
                        <button
                          onClick={() => importSale(sale)}
                          className="btn btn-primary btn-sm"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invoice Items */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{print_lang('invoice_items', 'Invoice Items')}</h3>
                <div className="flex gap-2 items-center">
                  {/* Treatment Search */}
                  <div className="relative">
                    <div className="flex">
                      <input
                        type="text"
                        placeholder={print_lang('search_treatment', 'Search treatment...')}
                        value={treatmentSearch}
                        onChange={(e) => {
                          setTreatmentSearch(e.target.value);
                          setShowTreatmentDropdown(true);
                        }}
                        onFocus={() => setShowTreatmentDropdown(true)}
                        className="w-64 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTreatmentDropdown(!showTreatmentDropdown)}
                        className="btn btn-primary btn-sm ml-1"
                      >
                        <Search size={16} />
                      </button>
                    </div>
                    {showTreatmentDropdown && (
                      <>
                        {/* Backdrop to close dropdown */}
                        <div
                          className="fixed inset-0 z-5"
                          onClick={() => setShowTreatmentDropdown(false)}
                        />
                        <div className="absolute z-10 mt-1 w-80 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto right-0">
                          {filteredTreatments.length === 0 ? (
                            <p className="p-3 text-gray-500 text-sm">
                              {treatmentSearch
                                ? print_lang('no_treatments_found', 'No treatments found')
                                : print_lang('type_to_search', 'Type to search treatments...')}
                            </p>
                          ) : (
                            filteredTreatments.slice(0, 20).map(treatment => (
                              <div
                                key={treatment.t_id}
                                onClick={() => handleAddTreatmentItem(treatment)}
                                className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium">{treatment.name}</p>
                                    {treatment.name_en && <p className="text-xs text-gray-500">{treatment.name_en}</p>}
                                  </div>
                                  <span className="text-blue-600 font-medium">{formatCurrency(treatment.price)} ฿</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Manual Add Button */}
                  <button onClick={handleAddManualItem} className="btn btn-secondary btn-sm">
                    <Plus size={16} className="mr-1" />
                    {print_lang('add_item', 'Add Item')}
                  </button>
                </div>
              </div>

              {invoiceForm.items.length === 0 ? (
                <p className="text-center text-gray-400 py-8">
                  {print_lang('no_items', 'No items added. Search treatments or add manually.')}
                </p>
              ) : (
                <div className="space-y-4">
                  {invoiceForm.items.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      {/* grid-cols-4 below 768px: at grid-cols-12 straight through, a
                          390px row squeezes Qty/Price to ~44px and the delete button
                          to ~22px - unusable number inputs and a clipped icon. The
                          4-col mobile grid wraps the same five fields into three full
                          rows (item; qty+price; discount+remove) and is untouched at
                          md: and up, where the columns still sum to the original 12. */}
                      <div className="grid grid-cols-4 md:grid-cols-12 gap-3">
                        <div className="col-span-4 md:col-span-5">
                          <label className="text-xs text-gray-500">{print_lang('item', 'Item')}</label>
                          {(item.reference_id || item.treatment_id) ? (
                            <p className="font-medium">{item.item_name}</p>
                          ) : (
                            <input
                              type="text"
                              value={item.item_name}
                              onChange={(e) => handleUpdateItem(index, 'item_name', e.target.value)}
                              placeholder={print_lang('item_name', 'Item name')}
                            />
                          )}
                          {item.item_description && (
                            <p className="text-sm text-gray-500">{item.item_description}</p>
                          )}
                          {item.treatment_id && (
                            <span className="text-xs text-blue-500">{print_lang('from_treatment_catalog', 'From treatment catalog')}</span>
                          )}
                        </div>
                        <div className="col-span-2 md:col-span-2">
                          <label className="text-xs text-gray-500">{print_lang('qty', 'Qty')}</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div className="col-span-2 md:col-span-2">
                          <label className="text-xs text-gray-500">{print_lang('price', 'Price')}</label>
                          <input
                            type="number"
                            min="0"
                            value={item.unit_price}
                            onChange={(e) => handleUpdateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="col-span-3 md:col-span-2">
                          <label className="text-xs text-gray-500">{print_lang('discount', 'Discount')}</label>
                          <div className="flex gap-1">
                            <input
                              type="number"
                              min="0"
                              value={item.discount_amount}
                              onChange={(e) => handleUpdateItem(index, 'discount_amount', parseFloat(e.target.value) || 0)}
                              className="w-full"
                            />
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-1 flex items-end">
                          <button
                            onClick={() => handleRemoveItem(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-end items-center gap-4 mt-2">
                        {item.vat_mode && item.vat_mode !== 'none' && (
                          <span className="text-sm text-gray-500">
                            {print_lang('vat', 'VAT')} ({item.vat_rate}%): {formatCurrency(calculateItemLine(item).vat)} ฿
                          </span>
                        )}
                        <span className="font-medium">
                          {print_lang('total', 'Total')}: {formatCurrency(calculateItemTotal(item))} ฿
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Summary & Actions */}
          <div className="space-y-6">
            {/* Date & Due Date */}
            <div className="card">
              <h3 className="text-lg font-bold mb-4">{print_lang('invoice_details', 'Invoice Details')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('date', 'Date')}</label>
                  <input
                    type="date"
                    value={invoiceForm.date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('due_date', 'Due Date')}</label>
                  <input
                    type="date"
                    value={invoiceForm.due_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Discounts & Tax */}
            <div className="card">
              <h3 className="text-lg font-bold mb-4">{print_lang('discounts_tax', 'Discounts & Tax')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('total_discount', 'Total Discount')}</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      value={invoiceForm.discount_amount}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, discount_amount: parseFloat(e.target.value) || 0 })}
                      className="flex-1"
                    />
                    <select
                      value={invoiceForm.discount_type}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, discount_type: e.target.value })}
                      className="w-20"
                    >
                      <option value="fixed">฿</option>
                      <option value="percentage">%</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('tax_rate', 'Tax Rate')} (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={invoiceForm.tax_rate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="card bg-gray-50">
              <h3 className="text-lg font-bold mb-4">{print_lang('summary', 'Summary')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>{print_lang('subtotal', 'Subtotal')}</span>
                  <span>{formatCurrency(totals.subtotal)} ฿</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>{print_lang('discount', 'Discount')}</span>
                    <span>-{formatCurrency(totals.discount)} ฿</span>
                  </div>
                )}
                {totals.taxAmount > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>{print_lang('vat', 'VAT')}</span>
                    <span>{formatCurrency(totals.taxAmount)} ฿</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold pt-2 border-t">
                  <span>{print_lang('grand_total', 'Grand Total')}</span>
                  <span className="text-blue-600">{formatCurrency(totals.amount)} ฿</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card">
              <label className="block text-sm font-medium mb-1">{print_lang('notes', 'Notes')}</label>
              <textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                rows={3}
                placeholder={print_lang('invoice_notes', 'Additional notes...')}
              />
            </div>

            {/* Actions */}
            <button
              onClick={handleSaveInvoice}
              disabled={isSaving || invoiceForm.items.length === 0}
              className="w-full btn btn-primary py-3 text-lg"
            >
              <Save size={20} className="inline mr-2" />
              {isSaving
                ? print_lang('saving', 'Saving...')
                : editingInvoice
                  ? print_lang('update_invoice', 'Update Invoice')
                  : print_lang('create_invoice', 'Create Invoice')
              }
            </button>
            {editingInvoice && (
              <button
                onClick={handleCancelEdit}
                className="w-full btn btn-secondary py-3 text-lg mt-2"
              >
                <X size={20} className="inline mr-2" />
                {print_lang('cancel', 'Cancel')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowInvoiceModal(false)}>
          <div className="modal-content w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileText size={24} />
                {selectedInvoice.invoice_number}
              </h2>
              <button onClick={() => setShowInvoiceModal(false)}>
                <X size={24} />
              </button>
            </div>

            {/* Invoice Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">{print_lang('patient', 'Patient')}</p>
                <p className="font-medium">{selectedInvoice.patient_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{print_lang('date', 'Date')}</p>
                <p className="font-medium">{formatDate(selectedInvoice.date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{print_lang('status', 'Status')}</p>
                {getStatusBadge(selectedInvoice.status)}
              </div>
              <div>
                <p className="text-sm text-gray-500">{print_lang('balance', 'Balance')}</p>
                <p className="font-medium text-red-600">
                  {formatCurrency(parseFloat(selectedInvoice.amount || 0) - parseFloat(selectedInvoice.paid_amount || 0))} ฿
                </p>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="mb-6">
              <h3 className="font-bold mb-3">{print_lang('items', 'Items')}</h3>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-2">{print_lang('item', 'Item')}</th>
                    <th className="text-right p-2">{print_lang('qty', 'Qty')}</th>
                    <th className="text-right p-2">{print_lang('price', 'Price')}</th>
                    <th className="text-right p-2">{print_lang('discount', 'Discount')}</th>
                    <th className="text-right p-2">{print_lang('vat', 'VAT')}</th>
                    <th className="text-right p-2">{print_lang('total', 'Total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2">
                        <p className="font-medium">{item.item_name}</p>
                        {item.item_description && <p className="text-sm text-gray-500">{item.item_description}</p>}
                      </td>
                      <td className="text-right p-2">{item.quantity}</td>
                      <td className="text-right p-2">{formatCurrency(item.unit_price)}</td>
                      <td className="text-right p-2 text-red-600">{formatCurrency(item.discount_amount)}</td>
                      <td className="text-right p-2">
                        {item.vat_mode && item.vat_mode !== 'none' ? formatCurrency(item.vat_amount) : '-'}
                      </td>
                      <td className="text-right p-2 font-medium">{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-end mt-4">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span>{print_lang('subtotal', 'Subtotal')}</span>
                    <span>{formatCurrency(parseFloat(selectedInvoice.amount || 0) + parseFloat(selectedInvoice.discount_amount || 0))} ฿</span>
                  </div>
                  {parseFloat(selectedInvoice.discount_amount) > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>{print_lang('discount', 'Discount')}</span>
                      <span>-{formatCurrency(selectedInvoice.discount_amount)} ฿</span>
                    </div>
                  )}
                  {(selectedInvoice.items || []).reduce((sum, it) => sum + (parseFloat(it.vat_amount) || 0), 0) > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>{print_lang('vat', 'VAT')}</span>
                      <span>
                        {formatCurrency((selectedInvoice.items || []).reduce((sum, it) => sum + (parseFloat(it.vat_amount) || 0), 0))} ฿
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>{print_lang('total', 'Total')}</span>
                    <span>{formatCurrency(selectedInvoice.amount)} ฿</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>{print_lang('paid', 'Paid')}</span>
                    <span>{formatCurrency(selectedInvoice.paid_amount)} ฿</span>
                  </div>
                  <div className="flex justify-between font-bold text-red-600">
                    <span>{print_lang('balance', 'Balance')}</span>
                    <span>{formatCurrency(parseFloat(selectedInvoice.amount || 0) - parseFloat(selectedInvoice.paid_amount || 0))} ฿</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment History */}
            {invoicePayments.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold mb-3">{print_lang('payment_history', 'Payment History')}</h3>
                <div className="space-y-2">
                  {invoicePayments.map((payment) => (
                    <div key={payment.payment_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div>
                        <p className="font-medium">{payment.receipt_number}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(payment.payment_date)} • {print_lang(payment.payment_method, payment.payment_method)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-green-600">{formatCurrency(payment.amount)} ฿</span>
                        <button
                          onClick={() => handlePrintReceipt(payment)}
                          className="p-2 hover:bg-green-100 rounded"
                        >
                          <Printer size={16} className="text-green-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => setShowInvoiceModal(false)} className="btn btn-secondary">
                {print_lang('close', 'Close')}
              </button>
              <button onClick={() => handlePrintInvoice(selectedInvoice)} className="btn btn-secondary">
                <Printer size={18} className="mr-2" />
                {print_lang('print_invoice', 'Print Invoice')}
              </button>
              {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                <button
                  onClick={() => {
                    setShowInvoiceModal(false);
                    handleOpenPayment(selectedInvoice);
                  }}
                  className="btn btn-primary"
                >
                  <DollarSign size={18} className="mr-2" />
                  {print_lang('record_payment', 'Record Payment')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <DollarSign size={24} />
                {print_lang('record_payment', 'Record Payment')}
              </h2>
              <button onClick={() => setShowPaymentModal(false)}>
                <X size={24} />
              </button>
            </div>

            {/* Invoice Summary */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-500">{print_lang('invoice', 'Invoice')}: {selectedInvoice.invoice_number}</p>
              <div className="flex justify-between mt-2">
                <span>{print_lang('total', 'Total')}</span>
                <span className="font-medium">{formatCurrency(selectedInvoice.amount)} ฿</span>
              </div>
              <div className="flex justify-between">
                <span>{print_lang('paid', 'Paid')}</span>
                <span className="text-green-600">{formatCurrency(selectedInvoice.paid_amount)} ฿</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t mt-2">
                <span>{print_lang('balance', 'Balance')}</span>
                <span className="text-red-600">{formatCurrency(parseFloat(selectedInvoice.amount || 0) - parseFloat(selectedInvoice.paid_amount || 0))} ฿</span>
              </div>
            </div>

            {/* Payment Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('amount', 'Amount')} *</label>
                <input
                  type="number"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">{print_lang('payment_method', 'Payment Method')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, payment_method: method.id })}
                        className={`p-3 border rounded-lg flex flex-col items-center gap-1 ${
                          paymentForm.payment_method === method.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <Icon size={20} />
                        <span className="text-xs">{method.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('payment_reference', 'Reference')}</label>
                <input
                  type="text"
                  value={paymentForm.payment_reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                  placeholder={print_lang('reference_placeholder', 'Transaction ID, check number...')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('date', 'Date')}</label>
                <input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{print_lang('notes', 'Notes')}</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button onClick={() => setShowPaymentModal(false)} className="btn btn-secondary">
                {print_lang('cancel', 'Cancel')}
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={isSaving}
                className="btn btn-primary"
              >
                <CheckCircle size={18} className="mr-2" />
                {isSaving ? print_lang('saving', 'Saving...') : print_lang('record_payment', 'Record Payment')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && (
        <div className="modal-overlay" onClick={() => setShowPrintModal(false)}>
          <div className="modal-content w-full max-w-4xl p-0 max-h-[95vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Toolbar */}
            <div className="flex justify-between items-center p-4 border-b bg-gray-50">
              <h2 className="text-lg font-bold">
                {printType === 'invoice'
                  ? `${print_lang('invoice', 'Invoice')}: ${selectedInvoice?.invoice_number}`
                  : `${print_lang('receipt', 'Receipt')}: ${selectedPayment?.receipt_number}`
                }
              </h2>
              <div className="flex gap-2">
                <button onClick={executePrint} className="btn btn-primary">
                  <Printer size={18} className="mr-2" />
                  {print_lang('print', 'Print')}
                </button>
                <button onClick={() => setShowPrintModal(false)} className="p-2 hover:bg-gray-200 rounded">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Nothing printed — red, because this is a failure to act on. */}
            {printError && (
              <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">
                {printError}
              </div>
            )}
            {/* It printed, just not silently. Amber: informational, not a failure. */}
            {printWarning && (
              <div className="bg-yellow-50 border-b border-yellow-200 text-yellow-800 px-4 py-2 text-sm">
                {printWarning}
              </div>
            )}

            {/* Print Preview */}
            <div className="overflow-auto p-4 bg-gray-200" style={{ maxHeight: 'calc(95vh - 80px)' }}>
              {/* A receipt is previewed as the very document that will be
                  printed — the same 80mm HTML, in an iframe. The A4 mock-up
                  that used to sit here looked nothing like the thermal output
                  it now produces, and a preview that lies about the paper is
                  worse than no preview. Invoices keep the A4 preview, which IS
                  what their print path serialises (via printRef). */}
              {printType === 'receipt' && selectedPayment ? (
                <div className="bg-white shadow-xl mx-auto" style={{ width: '80mm' }}>
                  <iframe
                    title="receipt-preview"
                    srcDoc={buildReceiptHtml(false)}
                    style={{ width: '80mm', height: '150mm', border: 'none', display: 'block' }}
                  />
                </div>
              ) : (
              <div className="bg-white shadow-xl mx-auto" style={{ width: '210mm', minHeight: '297mm' }} ref={printRef}>
                {printType === 'invoice' && selectedInvoice && (
                  <div className="document" style={{ padding: '10mm 15mm' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #000' }}>
                      <div>
                        {selectedInvoice.logo_path && (
                          <img src={`${API_URL}/${selectedInvoice.logo_path}`} alt="Logo" style={{ height: '60px' }} />
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{selectedInvoice.branch_name}</p>
                        <p style={{ fontSize: '12px' }}>{selectedInvoice.branch_address}</p>
                        <p style={{ fontSize: '12px' }}>โทร: {selectedInvoice.branch_phone}</p>
                        {parseFloat(selectedInvoice.tax_amount) > 0 && selectedInvoice.vat_number && (
                          <p style={{ fontSize: '12px' }}>เลขประจำตัวผู้เสียภาษี: {selectedInvoice.vat_number}</p>
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', margin: '20px 0', padding: '10px', background: '#f0f0f0' }}>
                      ใบแจ้งหนี้ / INVOICE
                    </div>

                    {/* Info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                      <div>
                        <p><strong>ลูกค้า / Customer:</strong></p>
                        <p>{selectedInvoice.patient_name}</p>
                        <p>{selectedInvoice.patient_address}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p><strong>เลขที่ / No.:</strong> {selectedInvoice.invoice_number}</p>
                        <p><strong>วันที่ / Date:</strong> {formatThaiDate(selectedInvoice.date)}</p>
                        <p><strong>สถานะ / Status:</strong> {print_lang(selectedInvoice.status, selectedInvoice.status)}</p>
                      </div>
                    </div>

                    {/* Items Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', margin: '20px 0' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>รายการ / Item</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', width: '60px' }}>จำนวน</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', width: '100px' }}>ราคา/หน่วย</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', width: '80px' }}>ส่วนลด</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', width: '80px' }}>VAT</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', width: '100px' }}>รวม</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedInvoice.items || []).map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {item.item_name}
                              {item.item_description && <br />}
                              {item.item_description && <small style={{ color: '#666' }}>{item.item_description}</small>}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{item.quantity}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: 'red' }}>{formatCurrency(item.discount_amount)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                              {item.vat_mode && item.vat_mode !== 'none' ? formatCurrency(item.vat_amount) : '-'}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatCurrency(item.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <table style={{ width: '250px' }}>
                        <tbody>
                          {selectedInvoice.discount_amount > 0 && (
                            <tr>
                              <td style={{ padding: '5px 10px' }}>ส่วนลด / Discount</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: 'red' }}>-{formatCurrency(selectedInvoice.discount_amount)}</td>
                            </tr>
                          )}
                          {(selectedInvoice.items || []).reduce((sum, it) => sum + (parseFloat(it.vat_amount) || 0), 0) > 0 && (
                            <tr>
                              <td style={{ padding: '5px 10px' }}>ภาษีมูลค่าเพิ่ม / VAT</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right' }}>
                                {formatCurrency((selectedInvoice.items || []).reduce((sum, it) => sum + (parseFloat(it.vat_amount) || 0), 0))}
                              </td>
                            </tr>
                          )}
                          {selectedInvoice.tax_amount > 0 && (
                            <tr>
                              <td style={{ padding: '5px 10px' }}>ภาษี / Tax</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right' }}>{formatCurrency(selectedInvoice.tax_amount)}</td>
                            </tr>
                          )}
                          <tr style={{ background: '#f0f0f0', fontWeight: 'bold', fontSize: '16px' }}>
                            <td style={{ padding: '10px' }}>รวมทั้งสิ้น / Grand Total</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(selectedInvoice.amount)} ฿</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '5px 10px' }}>ชำระแล้ว / Paid</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: 'green' }}>{formatCurrency(selectedInvoice.paid_amount)} ฿</td>
                          </tr>
                          <tr style={{ fontWeight: 'bold' }}>
                            <td style={{ padding: '5px 10px' }}>ยอดค้าง / Balance</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: 'red' }}>{formatCurrency(parseFloat(selectedInvoice.amount || 0) - parseFloat(selectedInvoice.paid_amount || 0))} ฿</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Amount in words */}
                    <div style={{ marginTop: '20px', padding: '10px', background: '#f9f9f9', border: '1px solid #ddd' }}>
                      <strong>จำนวนเงิน (ตัวอักษร):</strong> {numberToThaiWords(selectedInvoice.amount || 0)}
                    </div>

                    {/* Footer */}
                    <div style={{ marginTop: '50px', paddingTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ width: '40%', textAlign: 'center' }}>
                        <div style={{ borderTop: '1px solid #000', marginTop: '60px', paddingTop: '10px' }}>
                          ผู้รับบริการ / Customer
                        </div>
                      </div>
                      <div style={{ width: '40%', textAlign: 'center' }}>
                        <div style={{ borderTop: '1px solid #000', marginTop: '60px', paddingTop: '10px' }}>
                          ผู้รับเงิน / Authorized
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
